// Using manual script loading instead of usetiful-sdk package due to compilation issues

/**
 * Usetiful integration service for MaxAnalytics
 *
 * Usetiful is a digital adoption platform that provides:
 * - Interactive product tours
 * - Smart tooltips
 * - Onboarding checklists
 * - User guidance and help
 */

interface UsetifulTags {
  [key: string]: string | number | boolean;
}

// Extend Window interface to include Usetiful globals
declare global {
  interface Window {
    usetifulTags?: UsetifulTags;
    USETIFUL?: {
      user?: {
        setId: (id: string) => void;
      };
    };
  }
}

class UsetifulService {
  private isInitialized = false;
  private token: string | null = null;

  /**
   * Initialize Usetiful with the provided token by loading the script
   * @param token - Your Usetiful token from the dashboard
   */
  initialize(token: string): void {
    if (this.isInitialized) {
      console.warn('Usetiful has already been initialized');
      return;
    }

    if (!token) {
      console.error('Usetiful token is required for initialization');
      return;
    }

    try {
      this.loadUsetifulScript(token);
      this.token = token;
      this.isInitialized = true;
      console.log('✅ Usetiful initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Usetiful:', error);
    }
  }

  /**
   * Dynamically load the Usetiful script
   * @param token - Usetiful token
   */
  private loadUsetifulScript(token: string): void {
    // Check if script is already loaded
    if (document.querySelector('script[src*="usetiful.js"]')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'usetifulScript';
    script.type = 'text/javascript';
    script.async = true;
    script.dataset.token = token;
    script.src = `https://www.usetiful.com/dist/usetiful.js`;

    // Add script to head
    const head = document.head || document.getElementsByTagName('head')[0];
    head.appendChild(script);
  }

  /**
   * Set custom tags for user segmentation and personalization
   * @param tags - Key-value pairs for user attributes
   */
  setTags(tags: UsetifulTags): void {
    if (!this.isInitialized) {
      console.warn('Usetiful must be initialized before setting tags');
      return;
    }

    try {
      // Set tags on window object for Usetiful to pick up
      window.usetifulTags = window.usetifulTags || {};
      Object.assign(window.usetifulTags, tags);
      console.log('✅ Usetiful tags set:', tags);
    } catch (error) {
      console.error('❌ Failed to set Usetiful tags:', error);
    }
  }

  /**
   * Set user-specific tags based on MaxAnalytics user data
   * @param user - User object from MaxAnalytics
   */
  setUserTags(user: any): void {
    if (!user) return;

    const tags: UsetifulTags = {
      userId: user._id || user.id,
      userName: user.name,
      userEmail: user.email,
      isAdmin: user.isAdmin || false,
    };

    // Add user settings if available
    if (user.settings) {
      tags.includeGlobal = user.settings.includeGlobal || false;
      if (user.settings.activeAgeGroupIds?.length) {
        tags.activeAgeGroups = user.settings.activeAgeGroupIds.length;
      }
    }

    this.setTags(tags);
  }

  /**
   * Set company/organization specific tags
   * @param company - Company object from MaxAnalytics
   */
  setCompanyTags(company: any): void {
    if (!company) return;

    const tags: UsetifulTags = {
      companyId: company._id || company.id,
      companyName: company.name,
      sportType: company.sport || 'default',
    };

    if (company.logo) {
      tags.hasCustomLogo = true;
    }

    this.setTags(tags);
  }

  /**
   * Set role-based tags for user permissions
   * @param roles - Array of user roles
   */
  setRoleTags(roles: string[]): void {
    if (!roles?.length) {
      return;
    }

    const tags: UsetifulTags = {
      userRoles: roles.join(','),
      roleCount: roles.length,
      hasReports: roles.includes('REPORTS'),
      hasConfiguration: roles.includes('CONFIGURATION'),
      hasConfigDrills: roles.includes('CONFIG_DRILLS'),
      hasSkillsDrillsConfig: roles.includes('CONFIG_DRILLS'),
      hasUserManagement: roles.includes('USER_MANAGEMENT'),
      hasCheckIn: roles.includes('CHECK_IN'),
      hasScoring: roles.includes('SCORING'),
    };

    this.setTags(tags);
  }

  /**
   * Check if Usetiful is properly initialized
   */
  isReady(): boolean {
    return this.isInitialized && this.token !== null;
  }

  /**
   * Get current initialization status
   */
  getStatus(): { initialized: boolean; token: string | null } {
    return {
      initialized: this.isInitialized,
      token: this.token
    };
  }
}

// Export singleton instance
export const usetifulService = new UsetifulService();
export default usetifulService;
